import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { PriorityMaster } from '../../types/masterData';
import { Save, Plus, Trash2 } from 'lucide-react';

interface SLARuleEditorProps {
  priorities: PriorityMaster[];
  onUpdate: (id: string, data: Partial<PriorityMaster>) => Promise<unknown>;
  onCreate: (data: Partial<PriorityMaster>) => Promise<unknown>;
  onDelete: (id: string) => Promise<unknown>;
}

export const SLARuleEditor: React.FC<SLARuleEditorProps> = ({ priorities, onUpdate, onCreate, onDelete }) => {
  const [editing, setEditing] = useState<Record<string, { sla_hours: number; warning_threshold_pct: number; color_code?: string }>>({});
  const [isAdding, setIsAdding] = useState(false);
  const [newPrio, setNewPrio] = useState({ name: '', sla_hours: 24, warning_threshold_pct: 85, color_code: '#38bdf8' });

  const handleChange = (id: string, field: string, value: string | number) => {
    setEditing(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const handleSave = async (id: string) => {
    if (editing[id]) {
      await onUpdate(id, editing[id]);
      setEditing(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const handleAdd = async () => {
    if (!newPrio.name) return;
    await onCreate(newPrio);
    setNewPrio({ name: '', sla_hours: 24, warning_threshold_pct: 85, color_code: '#38bdf8' });
    setIsAdding(false);
  };

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-800">
              <th className="text-left py-3 px-3 font-semibold text-slate-400">Priority</th>
              <th className="text-left py-3 px-3 font-semibold text-slate-400">SLA Hours</th>
              <th className="text-left py-3 px-3 font-semibold text-slate-400">Warning %</th>
              <th className="text-left py-3 px-3 font-semibold text-slate-400">Color</th>
              <th className="text-right py-3 px-3 font-semibold text-slate-400 w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {priorities.map(prio => {
              const edits = editing[prio.priority_id];
              return (
                <tr key={prio.priority_id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: prio.color_code }} />
                      <span className="font-semibold text-slate-200">{prio.name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-3">
                    {edits ? (
                      <input type="number" value={edits.sla_hours} onChange={e => handleChange(prio.priority_id, 'sla_hours', Number(e.target.value))}
                        className="w-20 bg-slate-800 border border-slate-700 rounded p-1.5 text-xs text-slate-100" />
                    ) : (
                      <span className="text-sky-400 font-semibold">{prio.sla_hours}h</span>
                    )}
                  </td>
                  <td className="py-3 px-3">
                    {edits ? (
                      <input type="number" value={edits.warning_threshold_pct} onChange={e => handleChange(prio.priority_id, 'warning_threshold_pct', Number(e.target.value))}
                        className="w-20 bg-slate-800 border border-slate-700 rounded p-1.5 text-xs text-slate-100" />
                    ) : (
                      <span className="text-slate-300">{prio.warning_threshold_pct}%</span>
                    )}
                  </td>
                  <td className="py-3 px-3">
                    {edits ? (
                      <input type="color" value={edits.color_code || prio.color_code} onChange={e => handleChange(prio.priority_id, 'color_code', e.target.value)}
                        className="w-8 h-8 rounded cursor-pointer" />
                    ) : (
                      <span className="text-slate-400">{prio.color_code}</span>
                    )}
                  </td>
                  <td className="py-3 px-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {edits ? (
                        <Button variant="primary" size="sm" onClick={() => handleSave(prio.priority_id)} icon={<Save className="w-3 h-3" />}>Save</Button>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => setEditing(prev => ({ ...prev, [prio.priority_id]: { sla_hours: prio.sla_hours, warning_threshold_pct: prio.warning_threshold_pct } }))}>Edit</Button>
                      )}
                      <button onClick={() => onDelete(prio.priority_id)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isAdding ? (
        <div className="p-4 bg-slate-900/60 border border-sky-500/30 rounded-xl space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Input value={newPrio.name} onChange={e => setNewPrio(prev => ({ ...prev, name: e.target.value }))} placeholder="Priority name" />
            <Input type="number" value={String(newPrio.sla_hours)} onChange={e => setNewPrio(prev => ({ ...prev, sla_hours: Number(e.target.value) }))} placeholder="SLA hours" />
            <Input type="number" value={String(newPrio.warning_threshold_pct)} onChange={e => setNewPrio(prev => ({ ...prev, warning_threshold_pct: Number(e.target.value) }))} placeholder="Warning %" />
            <input type="color" value={newPrio.color_code} onChange={e => setNewPrio(prev => ({ ...prev, color_code: e.target.value }))} className="w-full h-10 rounded cursor-pointer" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setIsAdding(false)}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleAdd} icon={<Save className="w-3.5 h-3.5" />}>Save Priority</Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setIsAdding(true)} icon={<Plus className="w-3.5 h-3.5" />}>
          Add New Priority
        </Button>
      )}
    </div>
  );
};
